import React from 'react'
import { useSelector } from 'react-redux'

import {
  hasPermission,
  isSuperAdmin as isSuperAdminAccess,
  tabIdForMenu
} from 'app/permissions'

/**
 * The current user's Alfred access, as loaded from GET /base/me.
 *
 *   const { loaded, access, can } = useAccess()
 *
 * `loaded` is false until /base/me answers — gates must render a spinner rather
 * than treat "not loaded" as "no permission", or they will flash an empty
 * sidebar and bounce the user off whatever page they opened.
 */
export const useAccess = () => {
  const { loaded, access } = useSelector((state) => state.accessReducer)

  const can = React.useCallback((id) => hasPermission(access, id), [access])

  // Specifically the Super Admin ROLE -- not `unrestricted`, which is also true
  // for someone with NO role. Used to gate role editing on the people screens,
  // and superAdminOnly menu items. One definition, in app/permissions, so the
  // sidebar prune and the page guards cannot drift apart.
  const isSuperAdmin = isSuperAdminAccess(access)

  // Has a Teacher record. Preschool evaluation authoring (weekly + term) is open
  // to teachers as well as super-admins.
  const isTeacher = Boolean(access?.is_teacher)

  return { loaded, access, can, isSuperAdmin, isTeacher }
}

/**
 * Filter a page's local TABS array down to the tabs this user may see.
 *
 * In-page tab strips are not routes and each page owns its own TABS array, so
 * PrivateRoute can never gate them — this is the only seam. `menuName` is the
 * `name` of the menu item the page hangs off in config.js (e.g. 'users'), which
 * is how the tab ids in the permission catalogue are keyed.
 *
 * Tabs whose id is not in the catalogue are left visible: an unknown id means
 * the page declares a tab nobody registered, and hiding it would silently break
 * a screen. Fail open, like everything else here.
 */
export const useAllowedTabs = (
  menuName,
  tabs,
  getLabel = (tab) => tab.label
) => {
  const { loaded, access } = useAccess()

  return React.useMemo(() => {
    if (!loaded || !access || access.unrestricted) return tabs
    return tabs.filter((tab) => {
      const id = tabIdForMenu(menuName, getLabel(tab))
      if (!id) return true
      return hasPermission(access, id)
    })
  }, [loaded, access, menuName, tabs, getLabel])
}
